import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Terms of Service — AURA</title>
        <meta name="description" content="AURA Terms of Service - Rules and conditions for using our fashion intelligence platform." />
      </Helmet>
      
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to AURA
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-serif mb-4">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-12">Last updated: January 2025</p>
          
          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-serif mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using AURA ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. AURA is operated by BWE Studio ("Company," "we," "us," or "our").
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                AURA is an AI-powered fashion intelligence platform that allows users to digitize their wardrobe, receive personalized outfit recommendations, plan outfits, and engage with a style community. The Service is available on web and mobile platforms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">3. User Accounts</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You must be at least 13 years old to create an account</li>
                <li>You are responsible for maintaining the security of your account credentials</li>
                <li>You must provide accurate and complete registration information</li>
                <li>One person may not maintain multiple accounts</li>
                <li>You are responsible for all activities that occur under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">4. AURA+ Premium Subscription</h2>
              <h3 className="text-lg font-medium mb-2">Pricing</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                AURA+ Premium is available at ₹299/month. Prices may vary by region and are subject to change with notice.
              </p>
              
              <h3 className="text-lg font-medium mb-2">Billing</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Subscriptions are billed monthly through Razorpay</li>
                <li>Your subscription will auto-renew unless cancelled</li>
                <li>You may cancel at any time; access continues until the end of the billing period</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2 mt-4">Refunds</h3>
              <p className="text-muted-foreground leading-relaxed">
                We offer a 7-day money-back guarantee for new subscribers. Contact support within 7 days of your first payment for a full refund.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">5. User Content</h2>
              <h3 className="text-lg font-medium mb-2">Your Content</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You retain ownership of all photos and content you upload. By uploading content, you grant us a license to process it for AI analysis and display it within the Service.
              </p>
              
              <h3 className="text-lg font-medium mb-2">Content Guidelines</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Do not upload content that infringes others' intellectual property</li>
                <li>Do not upload inappropriate, offensive, or illegal content</li>
                <li>Do not upload content that violates others' privacy</li>
                <li>Only upload photos of your own wardrobe items</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">6. Acceptable Use</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to reverse engineer or copy any Service features</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Harass, bully, or intimidate other users</li>
                <li>Spam or send unsolicited communications</li>
                <li>Interfere with the Service's security or functionality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">7. AI-Generated Content</h2>
              <p className="text-muted-foreground leading-relaxed">
                Outfit recommendations and style suggestions are generated by artificial intelligence. While we strive for accuracy, AI suggestions are advisory only. We do not guarantee the accuracy, completeness, or suitability of AI-generated recommendations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">8. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                AURA, its logo, design, and all related intellectual property are owned by BWE Studio. You may not use our trademarks without prior written permission. The Service's technology, features, and algorithms are proprietary.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">9. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AURA AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">10. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">11. Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may suspend or terminate your account if you violate these Terms. You may delete your account at any time through the app settings. Upon termination, your data will be deleted in accordance with our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">12. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by the laws of India. Any disputes shall be resolved in the courts of Mumbai, Maharashtra.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">13. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms. We will notify you of material changes via email or in-app notification.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-serif mb-4">14. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                For questions about these Terms, contact us at:
              </p>
              <p className="text-muted-foreground mt-2">
                <strong>Email:</strong> legal@aura.style<br />
                <strong>Address:</strong> BWE Studio, Mumbai, India
              </p>
            </section>
          </div>
          
          <div className="mt-16 pt-8 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Built by{' '}
              <a 
                href="https://bwestudios.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="font-bold hover:text-foreground transition-colors"
              >
                BWE Studio
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
